function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { sharedPropTypes } from '@dhis2/ui-constants';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { debounce } from './debounce/debounce.js';
import { InputWrapper } from './input-wrapper.js';
import { MenuWrapper } from './menu-wrapper.js';

// Keycodes for the keypress event handlers
const ESCAPE_KEY = 27;
const SPACE_KEY = 32;
const UP_KEY = 38;
const DOWN_KEY = 40;
export class Select extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "state", {
      open: false,
      menuWidth: 'auto'
    });
    _defineProperty(this, "selectRef", /*#__PURE__*/React.createRef());
    _defineProperty(this, "inputRef", /*#__PURE__*/React.createRef());
    /**
     * We're debouncing this so it doesn't fire continually during a resize.
     *
     * Additionally we should use requestPostAnimationFrame to not trigger a forced
     * layout, but that's just a proposal, and the added complexity of solving this
     * in another manner does not seem worth it, considering the minor perf penalty.
     *
     * See: https://nolanlawson.com/2018/09/25/accurately-measuring-layout-on-the-web
     */
    _defineProperty(this, "onResize", debounce(() => {
      const menuWidth = this.getMenuWidth();
      if (this.state.menuWidth !== menuWidth) {
        this.setState({
          menuWidth
        });
      }
    }, 50));
    _defineProperty(this, "handleFocusInput", () => {
      this.inputRef.current.focus();
    });
    _defineProperty(this, "onFocus", e => {
      const {
        onFocus,
        disabled,
        selected
      } = this.props;
      if (disabled || !onFocus) {
        return;
      }
      onFocus({
        selected
      }, e);
    });
    _defineProperty(this, "handleOpen", () => {
      this.setState({
        open: true,
        menuWidth: this.getMenuWidth()
      });
    });
    _defineProperty(this, "handleClose", () => {
      this.setState({
        open: false
      });
    });
    _defineProperty(this, "onToggle", e => {
      if (this.props.disabled) {
        return;
      }
      e.stopPropagation();
      this.state.open ? this.handleClose() : this.handleOpen();
    });
    _defineProperty(this, "onOutsideClick", e => {
      const {
        onBlur,
        disabled,
        selected
      } = this.props;
      if (disabled) {
        return;
      }
      this.handleClose();
      if (onBlur) {
        onBlur({
          selected
        }, e);
      }
    });
    _defineProperty(this, "onKeyDown", e => {
      const {
        onKeyDown,
        disabled,
        selected
      } = this.props;
      if (disabled) {
        return;
      }
      if (onKeyDown) {
        onKeyDown({
          selected
        }, e);
      }
      if (e.defaultPrevented) {
        return;
      }
      e.stopPropagation();
      const {
        open
      } = this.state;
      const {
        keyCode
      } = e;
      const shouldOpen = !open && (keyCode === SPACE_KEY || keyCode === UP_KEY || keyCode === DOWN_KEY);
      const shouldClose = open && keyCode === ESCAPE_KEY;
      if (shouldClose) {
        return this.handleClose();
      }
      if (shouldOpen) {
        return this.handleOpen();
      }
    });
  }
  componentDidMount() {
    if (this.props.initialFocus) {
      this.inputRef.current.focus();
    }
    this.setState({
      menuWidth: this.getMenuWidth()
    });
    window.addEventListener('resize', this.onResize);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }
  getMenuWidth() {
    const {
      offsetWidth
    } = this.inputRef.current;
    const {
      menuWidth
    } = this.state;
    if (offsetWidth && `${offsetWidth}px` !== menuWidth) {
      return `${offsetWidth}px`;
    }
    return menuWidth;
  }
  render() {
    const {
      open,
      menuWidth
    } = this.state;
    const {
      children,
      className,
      selected,
      onChange,
      tabIndex,
      maxHeight,
      error,
      warning,
      valid,
      disabled,
      dense,
      dataTest
    } = this.props;

    // Create the input
    const inputProps = {
      selected,
      onChange,
      options: children,
      disabled
    };
    const input = /*#__PURE__*/React.cloneElement(this.props.input, inputProps);

    // Create the menu
    const menuProps = {
      selected,
      onChange,
      options: children,
      handleClose: this.handleClose,
      handleFocusInput: this.handleFocusInput
    };
    const menu = /*#__PURE__*/React.cloneElement(this.props.menu, menuProps);
    return /*#__PURE__*/React.createElement("div", {
      className: className,
      ref: this.selectRef,
      onFocus: this.onFocus,
      onKeyDown: this.onKeyDown,
      "data-test": dataTest
    }, /*#__PURE__*/React.createElement(InputWrapper, {
      onToggle: this.onToggle,
      inputRef: this.inputRef,
      tabIndex: disabled ? '-1' : tabIndex,
      error: error,
      warning: warning,
      valid: valid,
      disabled: disabled,
      dense: dense,
      dataTest: `${dataTest}-input`
    }, input), open && /*#__PURE__*/React.createElement(MenuWrapper, {
      onClick: this.onOutsideClick,
      maxHeight: maxHeight,
      selectRef: this.selectRef,
      menuWidth: menuWidth,
      dataTest: `${dataTest}-menu`
    }, menu));
  }
}
_defineProperty(Select, "defaultProps", {
  dataTest: 'dhis2-uicore-select'
});
Select.propTypes = {
  input: PropTypes.element.isRequired,
  menu: PropTypes.element.isRequired,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  dataTest: PropTypes.string,
  dense: PropTypes.bool,
  disabled: PropTypes.bool,
  error: sharedPropTypes.statusPropType,
  initialFocus: PropTypes.bool,
  maxHeight: PropTypes.string,
  tabIndex: PropTypes.string,
  valid: sharedPropTypes.statusPropType,
  warning: sharedPropTypes.statusPropType,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func
};